import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { AuthActions, UserActions } from '@portal/actions';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import MessageModal from '@portal/components/MessageModal/MessageModal';
import SmsCode from '@portal/components/SmsCode/SmsCode';

import {
  MessageOutlined,
  RightOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import SecurityIcon from '@portal/assets/svg/ic_new_security.svg?react';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import Loading from '@portal/components/Loading/Loading';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { TokenMethod } from '@portal/enum/device';
import { useNavigation } from '@portal/hooks/useNavigation';
import useReduxState from '@portal/hooks/useReduxState';
import { UserCredentialType } from '@portal/models/enumerators/auth';
import { translate } from '@portal/services/i18n';
import * as MessageService from '@portal/services/message';

const ChangePin = () => {
  const navigate = useNavigation();
  const dispatch = useDispatch();

  const {
    user: {
      me: { mustChangePin },
    },
    auth: { changeUserCredentialFail, changeUserCredential },
    loading: { amount },
    theme: { currentTheme },
    device: { lastNumbers },
  } = useReduxState();

  const [disableButton, setDisableButton] = useState(true);
  const [code, setCode] = useState('');
  const [
    isChangeCredentialMessageModalVisible,
    setIsChangeCredentialMessageModalVisible,
  ] = useState(false);
  const [isPinModalVisible, setIsPinModalVisible] = useState(false);
  const [isChannelSelected, setIsChannelSelected] = useState(false);
  const [method, setMethod] = useState<TokenMethod>(TokenMethod.SMS);

  useEffect(() => {
    if (isChannelSelected && !isPinModalVisible) {
      dispatch(AuthActions.userCredentialUpdateToken(method));
    }
  }, [dispatch, isChannelSelected, isPinModalVisible, method]);

  useEffect(() => {
    if (changeUserCredentialFail) {
      MessageService.error(changeUserCredentialFail.message, {
        originalError: changeUserCredentialFail,
      });

      setIsPinModalVisible(false);
      dispatch(AuthActions.userCredentialError(null));
    }
  }, [changeUserCredentialFail, dispatch]);

  useEffect(() => {
    if (changeUserCredential) {
      dispatch(UserActions.getMeFirstLoad());
      dispatch(AuthActions.userCredentialError(null));
      dispatch(AuthActions.userCredentialSuccess(false));
      navigate('/inicio');
    }
  }, [changeUserCredential, dispatch, navigate]);

  useEffect(() => {
    if (mustChangePin) {
      setIsChangeCredentialMessageModalVisible(true);
      setIsChannelSelected(false);
      setIsPinModalVisible(false);
    }
  }, [navigate, mustChangePin]);

  useEffect(() => {
    setDisableButton(code.length < 6);
  }, [code.length]);

  const handleSelectChannel = (selectedMethod: TokenMethod) => {
    setMethod(selectedMethod);
    setIsChannelSelected(true);
  };

  const handleResend = (selectedMethod?: TokenMethod) => {
    const methodToUse = selectedMethod || method;
    setMethod(methodToUse);
    dispatch(AuthActions.userCredentialUpdateToken(methodToUse));
  };

  const handleSubmit = (pin: string) => {
    if (!isPinModalVisible) {
      setIsPinModalVisible(true);
    } else {
      dispatch(
        AuthActions.userCredentialChangeCredential({
          type: UserCredentialType.PIN,
          credential: pin,
          token: code,
        })
      );
    }
  };

  const handleLogout = () => {
    if (!isChannelSelected) {
      dispatch(AuthActions.logout());
    } else {
      setIsChannelSelected(false);
      setCode('');
    }
  };

  return (
    <>
      {amount > 0 && <Loading />}

      <div
        className="change-credentials"
        style={{ background: currentTheme.background }}
      >
        <div className="change-credentials__form">
          <div className="change-credentials__form__icon-security">
            <div className="change-credentials__form__icon-security__circle">
              <img
                src="/assets/svg/lock.svg"
                alt={translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SECURE')}
                className="change-credentials__form__icon-security__icon"
              />
            </div>
          </div>

          <ThemedText variant="h2" className="change-credentials__form__header">
            {translate(
              !isChannelSelected
                ? 'PAGES.AUTH.CHANGE_CREDENTIALS.PIN.CHANNEL.TITLE'
                : `PAGES.AUTH.CHANGE_CREDENTIALS.PIN.TITLE_${
                    method === TokenMethod.WHATSAPP ? 'WHATSAPP' : 'SMS'
                  }`
            )}
          </ThemedText>

          <ThemedText
            variant="body"
            className="change-credentials__form__subtitle"
          >
            {translate(
              !isChannelSelected
                ? 'PAGES.AUTH.CHANGE_CREDENTIALS.PIN.CHANNEL.TEXT'
                : `PAGES.AUTH.CHANGE_CREDENTIALS.PIN.TEXT_${
                    method === TokenMethod.WHATSAPP ? 'WHATSAPP' : 'SMS'
                  }`
            )}
          </ThemedText>

          <div className="change-credentials__form__secure-badge">
            <SecurityIcon
              fill={currentTheme.text}
              className="change-credentials__form__secure-badge__icon"
            />
            <span>
              {translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SECURE')}
            </span>
          </div>
          {!isChannelSelected ? (
            <div
              className="change-credentials__form__channel-selection"
              key="step-channel"
            >
              <div className="change-credentials__form__channel-selection__options">
                <div
                  className="change-credentials__form__channel-selection__option-card"
                  onClick={() => handleSelectChannel(TokenMethod.SMS)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectChannel(TokenMethod.SMS);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={translate(
                    'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SMS_BUTTON'
                  )}
                >
                  <div className="change-credentials__form__channel-selection__option-card__icon change-credentials__form__channel-selection__option-card__icon--sms">
                    <MessageOutlined />
                  </div>
                  <div className="change-credentials__form__channel-selection__option-card__content">
                    <div className="change-credentials__form__channel-selection__option-card__title">
                      {translate(
                        'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SMS_BUTTON'
                      )}
                    </div>
                    <div className="change-credentials__form__channel-selection__option-card__description">
                      {translate(
                        'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SUBTITLE',
                        {
                          lastNumbers: lastNumbers || '',
                        }
                      )}
                    </div>
                  </div>
                  <RightOutlined className="change-credentials__form__channel-selection__option-card__arrow" />
                </div>
                <div
                  className="change-credentials__form__channel-selection__option-card"
                  onClick={() => handleSelectChannel(TokenMethod.WHATSAPP)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectChannel(TokenMethod.WHATSAPP);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={translate(
                    'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.WHATSAPP_BUTTON'
                  )}
                >
                  <div className="change-credentials__form__channel-selection__option-card__icon change-credentials__form__channel-selection__option-card__icon--whatsapp">
                    <WhatsAppOutlined />
                  </div>
                  <div className="change-credentials__form__channel-selection__option-card__content">
                    <div className="change-credentials__form__channel-selection__option-card__title">
                      {translate(
                        'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.WHATSAPP_BUTTON'
                      )}
                    </div>
                    <div className="change-credentials__form__channel-selection__option-card__description">
                      {translate(
                        'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SUBTITLE',
                        {
                          lastNumbers: lastNumbers || '',
                        }
                      )}
                    </div>
                  </div>
                  <RightOutlined className="change-credentials__form__channel-selection__option-card__arrow" />
                </div>
              </div>
            </div>
          ) : (
            <div className="change-credentials__form__step" key="step-code">
              <SmsCode
                code={code}
                setCode={setCode}
                onClickResend={handleResend}
                method={method}
              />
            </div>
          )}

          <ConfirmationModal
            handleConfirmation={handleSubmit}
            visible={isPinModalVisible}
            setVisible={setIsPinModalVisible}
            title={translate(
              'PAGES.AUTH.CHANGE_CREDENTIALS.PIN.PIN_MODAL.TITLE'
            )}
            text={translate('PAGES.AUTH.CHANGE_CREDENTIALS.PIN.PIN_MODAL.TEXT')}
          />

          <div className="change-credentials__form__actions">
            {isChannelSelected ? (
              <AdvancedButton
                disabled={disableButton}
                fullWidth
                text={translate('PAGES.AUTH.CHANGE_CREDENTIALS.PIN.OK')}
                onClick={() => handleSubmit('')}
              />
            ) : null}
            <AdvancedButton
              variant="outlined"
              fullWidth
              text={translate(
                !isChannelSelected
                  ? 'PAGES.AUTH.CHANGE_CREDENTIALS.PIN.CHANNEL.CANCEL'
                  : 'PAGES.AUTH.CHANGE_CREDENTIALS.PIN.CANCEL'
              )}
              onClick={handleLogout}
            />
          </div>
        </div>
      </div>

      <MessageModal
        title={translate('PAGES.AUTH.CHANGE_CREDENTIALS.PIN.MODAL.TITLE')}
        message={translate('PAGES.AUTH.CHANGE_CREDENTIALS.PIN.MODAL.MESSAGE')}
        buttonOkText={translate('PAGES.AUTH.CHANGE_CREDENTIALS.PIN.MODAL.OK')}
        onConfirm={() => setIsChangeCredentialMessageModalVisible(false)}
        setVisible={setIsChangeCredentialMessageModalVisible}
        visible={isChangeCredentialMessageModalVisible}
      />
    </>
  );
};

export default ChangePin;
